import { useEffect, useState } from "react";
import { Button, Collapse, ProgressBar, Spinner } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import api, { mediaUrl } from "../../api/axios";
import "./ActivationGuide.css";

const preferenceKey = () => {
  try { return "cardora:activationGuide:" + (JSON.parse(localStorage.getItem("user") || "null")?.id || "session"); }
  catch { return "cardora:activationGuide:session"; }
};
const readPreference = key => localStorage.getItem(key) || "";

function ActivationGuide({ progress: suppliedProgress, expandedDefault = false }) {
  const location = useLocation();
  const storageKey = preferenceKey();
  const [preference, setPreference] = useState(() => readPreference(storageKey));
  const [progress, setProgress] = useState(suppliedProgress || null);
  const [loading, setLoading] = useState(!suppliedProgress);
  const [matches, setMatches] = useState([]);
  const [matchStatus, setMatchStatus] = useState("idle");
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    const sync = () => setPreference(readPreference(storageKey));
    sync();
    window.addEventListener("cardora:activation-preference", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("cardora:activation-preference", sync);
      window.removeEventListener("storage", sync);
    };
  }, [storageKey]);

  const setVisibility = value => {
    localStorage.setItem(storageKey, value);
    setPreference(value);
    window.dispatchEvent(new Event("cardora:activation-preference"));
  };

  useEffect(() => {
    if (suppliedProgress) { setProgress(suppliedProgress); setLoading(false); return undefined; }
    if (!localStorage.getItem("token")) { setLoading(false); return undefined; }
    let cancelled = false;
    api.get("/users/me/onboarding-progress")
      .then(({ data }) => { if (!cancelled) setProgress(data); })
      .catch(() => { if (!cancelled) setProgress(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [suppliedProgress, location.pathname]);

  const steps = progress?.steps || [];
  const nextStep = steps.find(step => !step.completed);
  const hasCards = Boolean(steps.find(step => step.key === "card")?.completed);
  const complete = progress?.isComplete || (steps.length > 0 && steps.every(step => step.completed));
  useEffect(() => {
    if (!progress?.enabled || !complete) return;
    localStorage.setItem(storageKey, "hidden");
    setPreference("hidden");
    window.dispatchEvent(new Event("cardora:activation-preference"));
  }, [complete, progress?.enabled, storageKey]);
  const hidden = preference === "hidden" || (complete && !preference);
  const expanded = !hidden && (preference === "expanded" || (!preference && (!hasCards || expandedDefault)));
  useEffect(() => {
    if (!progress?.enabled || !hasCards || hidden) return undefined;
    const controller = new AbortController();
    setMatchStatus("loading");
    api.get("/social/opportunities", { signal: controller.signal })
      .then(({ data }) => {
        if (controller.signal.aborted) return;
        const items = Array.isArray(data) ? data : data.opportunities || [];
        setMatches(items.filter(item => item.cards_i_want?.length || item.cards_they_want?.length));
        setMatchStatus("ready");
      })
      .catch(() => { if (!controller.signal.aborted) setMatchStatus("error"); });
    return () => controller.abort();
  }, [progress?.enabled, hasCards, hidden, suppliedProgress, retry]);

  const track = action => {
    void api.post("/analytics/events", { eventType: "activation_action_clicked", metadata: { source: "web", action } }).catch(() => {});
  };
  if (loading) return <div className="activation-guide is-loading"><Spinner size="sm" /><span>Preparando tu coleccion...</span></div>;
  if (!progress?.enabled) return null;
  if (hidden) return <div className="activation-guide-recovery"><button type="button" onClick={() => setVisibility("expanded")}><i aria-hidden="true" className="bi bi-compass" /> Primeros pasos</button></div>;

  const completed = steps.filter(step => step.completed).length;
  const percentage = steps.length ? Math.round(completed / steps.length * 100) : 0;
  const hasMatches = matchStatus === "ready" && matches.length > 0;
  const sharePath = `${progress.binderPath || "/binder"}${progress.binderPath?.includes("?") ? "&" : "?"}action=share`;
  const primaryPath = hasCards ? hasMatches ? "/trades?tab=matches" : sharePath : nextStep?.path || "/binder";
  const primaryLabel = hasCards ? hasMatches ? "Ver coincidencias" : "Compartir binder" : nextStep?.actionLabel || "Continuar";
  const primaryAction = hasCards ? hasMatches ? "matches" : "share" : nextStep?.key || "binder";

  return (
    <section className={`activation-guide ${expanded ? "is-expanded" : "is-compact"}`} aria-label="Primeros pasos en Cardora">
      <div className="activation-guide-header">
        <button className="activation-guide-toggle" type="button" onClick={() => setVisibility(expanded ? "collapsed" : "expanded")} aria-expanded={expanded} aria-label={expanded ? "Plegar primeros pasos" : "Abrir primeros pasos"}>
          <i aria-hidden="true" className={`bi ${hasMatches ? "bi-stars" : "bi-compass"}`} />
          <span><strong>{hasCards ? hasMatches ? "Tienes coincidencias de cartas" : "Tu binder esta en marcha" : nextStep?.title || "Prepara tu coleccion"}</strong><small>{hasCards ? hasMatches ? `${matches.length} personas con cartas en comun` : "Comparte tu coleccion" : `${completed} de ${steps.length} pasos`}</small></span>
          <i aria-hidden="true" className={`bi bi-chevron-${expanded ? "up" : "down"}`} />
        </button>
        <button className="activation-guide-dismiss" type="button" title="Ocultar primeros pasos" aria-label="Ocultar primeros pasos" onClick={() => setVisibility("hidden")}><i aria-hidden="true" className="bi bi-x-lg" /></button>
      </div>
      <div className="activation-guide-actions">
        <Button as={Link} to={primaryPath} onClick={() => track(primaryAction)} className="btn-cardora activation-guide-action"><i aria-hidden="true" className={`bi ${primaryAction === "share" ? "bi-share" : "bi-arrow-right"}`} />{primaryLabel}</Button>
        {hasCards && <Link className="activation-guide-secondary" to={hasMatches ? sharePath : "/trades?tab=matches"} onClick={() => track(hasMatches ? "share" : "matches")}><i aria-hidden="true" className={`bi ${hasMatches ? "bi-share" : "bi-stars"}`} />{hasMatches ? "Compartir" : "Coincidencias"}</Link>}
      </div>
      <Collapse in={expanded} mountOnEnter unmountOnExit>
        <div>
          <div className="activation-guide-progress"><ProgressBar now={percentage} aria-label={`Progreso al ${percentage}%`} /><small>{completed} de {steps.length} pasos</small></div>
          <div className="activation-guide-steps">
            {steps.map(step => <Link className={step.completed ? "is-complete" : ""} to={step.path} key={step.key} onClick={() => track(step.key)}>
              <i aria-hidden="true" className={`bi ${step.completed ? "bi-check-circle-fill" : "bi-circle"}`} />
              <span><strong>{step.title}{step.optional ? " (opcional)" : ""}</strong><small>{step.description}</small></span>
              {!step.completed && <i aria-hidden="true" className="bi bi-chevron-right" />}
            </Link>)}
          </div>
          {hasCards && <div className="activation-guide-results" aria-live="polite">
            {matchStatus === "loading" && <p><Spinner as="span" size="sm" /> Revisando coincidencias...</p>}
            {matchStatus === "error" && <p>No pudimos revisar coincidencias. <button type="button" onClick={() => setRetry(value => value + 1)}>Reintentar</button></p>}
            {hasMatches && matches.slice(0, 3).map(person => <Link className="activation-match-person" key={person.user_id} to="/trades?tab=matches" onClick={() => track("matches")}>
              {person.avatar_url ? <img src={mediaUrl(person.avatar_url)} alt="" /> : <i aria-hidden="true" className="bi bi-person-circle" />}
              <span><b>{person.display_name}</b><small>{person.cards_i_want?.length ? `Tiene ${person.cards_i_want.length} carta${person.cards_i_want.length === 1 ? "" : "s"} que buscas` : `Busca ${person.cards_they_want.length} carta${person.cards_they_want.length === 1 ? "" : "s"} tuya${person.cards_they_want.length === 1 ? "" : "s"}`}</small></span><i aria-hidden="true" className="bi bi-chevron-right" />
            </Link>)}
          </div>}
        </div>
      </Collapse>
    </section>
  );
}

export default ActivationGuide;
